import { Announcements } from "@/features/messages/Announcements";
import { ensureResourceEmailContract } from "@/features/resource-email/cache";
import { ClientMetadataBootstrap } from "@/features/client-metadata/Bootstrap";
import {
  createFileRoute,
  Navigate,
  Outlet,
  redirect,
  useLocation,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  accountAccessDestination,
  accountAccessOptions,
} from "@/features/auth/access";
import { LegalPolicyGate } from "@/features/legal/components/legal-policy";
import {
  AccessError,
  AccessPending,
} from "@/features/auth/components/AccessStatus";

function ProtectedLayout() {
  const { session } = Route.useRouteContext();
  const location = useLocation();
  const account = useQuery({
    ...accountAccessOptions(session.user.id),
    refetchOnMount: false,
  });
  // A background recheck must not unmount the already-authorized page.
  if (account.isPending) return <AccessPending />;
  const destination = accountAccessDestination(account.data, account.error, {
    actorId: session.user.id,
    pathname: location.pathname,
  });
  if (destination)
    return (
      <Navigate
        to={destination}
        search={destination === "/auth/sign-in" ? { next: location.href } : {}}
        replace
      />
    );
  if (account.error) return <AccessError error={account.error} />;
  const content = (
    <>
      <ClientMetadataBootstrap actorId={session.user.id} />
      <Announcements key={session.user.id} actorId={session.user.id} />
      <Outlet />
    </>
  );
  // Database-confirmed administrators always bypass the legal gate. This never records acceptance.
  if (account.data?.id === session.user.id && account.data.role === "ADMIN")
    return content;
  return <LegalPolicyGate userId={session.user.id}>{content}</LegalPolicyGate>;
}

export const Route = createFileRoute("/_authenticated/_protected")({
  beforeLoad: async ({ context, location }) => {
    let destination: ReturnType<typeof accountAccessDestination>;
    try {
      const account = await context.queryClient.fetchQuery(
        accountAccessOptions(context.session.user.id),
      );
      destination = accountAccessDestination(account, null, {
        actorId: context.session.user.id,
        pathname: location.pathname,
      });
    } catch (error) {
      destination = accountAccessDestination(undefined, error);
      if (!destination) throw error;
    }
    if (destination)
      throw redirect({
        to: destination,
        search: destination === "/auth/sign-in" ? { next: location.href } : {},
        replace: true,
      });
    await ensureResourceEmailContract(context.queryClient, context.session.user.id);
  },
  component: ProtectedLayout,
  pendingComponent: AccessPending,
  errorComponent: AccessError,
});
